/**
 * "Why do these disagree?": the Lab's structured comparison, pressed from an
 * objection's card.
 *
 * THE SAME PATH. The press runs `compareClaimPair`, the function the Tests tab's
 * "Structure disagreement" button runs, on the objection and the claim it hangs
 * off (`disagreePairOf`). It is imported when pressed, because the Lab loads
 * lazily and a card that imported it statically would put the whole Lab in front
 * of every visitor's first paint. The comparison is stored where the Lab stores
 * it, and both claims' cards read it from there (`core/disagreement.ts`).
 *
 * PRICED as one move on the SELECTED model. A research pass runs on the exact
 * selection (`resolveModel`), not on a routed role, so its figure is
 * `projectVerbUsd`'s move arithmetic asked with no routing at all — which prices
 * the selection. The shape fits by construction rather than by luck. Output: the
 * pass requests `maxTokens` 12,000, exactly the `TOKENS_PER_MOVE` the move
 * projection charges as output, and its reasoning is billed inside that cap.
 * Input: the prompt's fixed part (instructions and schema) is about 9,100
 * characters, and the two claims' dated source records are capped at 24,000
 * more plus a seed survey of at most 9,000 — about 43,000 characters at the very
 * most, which the projection's 12,000 input tokens, charged at the 1.25×
 * cache-write rate, cover at three characters a token. A typical pass sends a
 * fraction of that. So the figure leans high like every other one, and like
 * every other one it is an estimate, never a charge (invariant 5).
 * `disagreePress.test.ts` holds both halves against the engine's own request.
 * No voice line rides on it: the comparison is a Lab analysis, which the voice
 * does not narrate (`passLanded`), so the pass is all the press buys (D295).
 *
 * IT DOES NOT WAIT ON UP NEXT, and refuses under the lock in the line's own
 * sentence instead. The line holds one verb on one claim and re-judges it, when
 * its turn comes, against the ROLE that verb routes to (`upNextRole`,
 * `resolveFor`) and that role's figure (`upNextPriceVerb`); this press is two
 * claims on the selection, which is no role, so the drain would re-judge it
 * against a model and a figure it will not run on. And the drain takes the next
 * item the moment the lock falls, trusting the verb it fires to take the lock
 * synchronously; this press reaches its runner through the Lab's lazy import, so
 * a deferred one would leave the lock free across an await with the line
 * already moving. The cross-examination refuses under the lock for the same
 * kind of reason (D167). A press made now either runs or says why.
 */

import type { ContradictionAnalysis } from '../core/contradiction'
import { COMPARISON_VERDICT_LABEL } from '../core/vocabulary'
import { disagreePairOf } from '../core/disagreement'
import { PROVIDERS } from '../core/models'
import { activeEntry, resolveModel, useStore } from '../core/store'
import { ONE_THOUGHT_PASS } from '../core/upNext'
import { projectVerbUsd } from '../engine/run'

type StoreState = ReturnType<typeof useStore.getState>

/** What the refusal reads: the lock, a live run, the document and the key. */
export type DisagreeView = Pick<
  StoreState,
  'busy' | 'run' | 'session' | 'modelId' | 'keys' | 'customBase' | 'customModel'
>

/**
 * The figure on the button: one move-shaped pass on the selection, or null
 * where the selection has no list rate — no figure rather than a wrong one.
 */
export const disagreeEstimate = (s: Pick<StoreState, 'modelId'>): number | null =>
  projectVerbUsd({ modelId: s.modelId, roleModels: {} }, 'move', null, { voice: false })

/** Whether the refusal is the key's, which a press answers by opening the key
 *  sheet, as every other verb's keyless press does. */
export const disagreeKeyless = (s: DisagreeView): boolean => resolveModel(s) === null

/**
 * Why pressing it now would not run, in the sentence the reader gets — or null.
 * The card greys the button on the lock and prints this as its title; the press
 * asks it again, and again after the Lab's import, which is the one await
 * between the press and the op that takes the lock.
 */
export function disagreeRefusal(s: DisagreeView, nodeId: string): string | null {
  const node = s.session.nodes[nodeId]
  if (!node || !disagreePairOf(s.session, node))
    return 'That objection, or the claim it answers, is no longer on the graph.'
  // Any pass in flight, the worked example's unfurl included — the checks'
  // rule, since this press does not stop the unfurl as a move does — and a live
  // run, which owns what comes next.
  if (s.busy || (s.run !== null && s.run.phase !== 'ended')) return ONE_THOUGHT_PASS
  if (disagreeKeyless(s))
    return `The comparison runs on the model you selected, so it needs your ${PROVIDERS[activeEntry(s).provider].name} key.`
  return null
}

/**
 * One press: the comparison on this objection and its claim, now, or the reason
 * it cannot run. Resolves when the pass has landed or failed.
 */
export async function pressDisagree(nodeId: string): Promise<void> {
  const s = useStore.getState()
  const refusal = disagreeRefusal(s, nodeId)
  if (refusal) {
    s.toast(refusal, 'warn')
    // The selection's house, because the selection is what this press runs on.
    if (disagreeKeyless(s) && s.session.nodes[nodeId]) {
      const on = activeEntry(s)
      s.askForKey(on.provider, 'missing', { what: 'The comparison', on: on.name })
    }
    return
  }
  let lab: typeof import('./ResearchLab')
  try {
    lab = await import('./ResearchLab')
  } catch {
    s.toast('The comparison could not load. Check the connection and press it again.', 'warn')
    return
  }
  // The import was an await: another press may have taken the lock inside it.
  const now = useStore.getState()
  const again = disagreeRefusal(now, nodeId)
  if (again) {
    now.toast(again, 'warn')
    return
  }
  const node = now.session.nodes[nodeId]!
  const pair = disagreePairOf(now.session, node)!
  const result = await lab.compareClaimPair(pair.leftId, pair.rightId)
  const after = useStore.getState()
  if (result.analysis)
    after.toast(
      `Why these disagree: ${COMPARISON_VERDICT_LABEL[result.analysis.verdict]}. It is on both claims’ cards, and in full in the Lab’s Tests tab.`,
      'good',
    )
  else after.toast(result.message, 'warn')
}

/** How many frames the door waits for the Lab to draw the comparison: the Lab
 *  loads lazily, so on a first opening its chunk arrives some frames after the
 *  press, and a door that gave up at the card editor's six would open the tab
 *  at its top every first time. */
const LAB_FRAMES = 120

/**
 * The block's door: the Lab's Tests tab, opened on this comparison.
 *
 * The tab lists every comparison newest first under its instruments, so opening
 * the tab alone left the record the reader asked for a long scroll below. This
 * finds the Lab's own article for the pair — the newest whose two quoted claims
 * are this comparison's, in its order — and brings it to the top. Found by what
 * the Lab prints rather than by an id the Lab would have to carry for the card;
 * if it is not there (a later comparison of other words), the tab still opens.
 */
export function openComparisonInLab(analysis: Pick<ContradictionAnalysis, 'left' | 'right'>): void {
  useStore.getState().setResearchOpen(true, 'tests')
  let frames = 0
  const seat = () => {
    const found = [...document.querySelectorAll<HTMLElement>('.research-analysis')].find((row) => {
      const quoted = [...row.querySelectorAll('.research-claim-pair blockquote')].map(
        (q) => q.textContent,
      )
      return quoted[0] === analysis.left.claim && quoted[1] === analysis.right.claim
    })
    if (!found && ++frames < LAB_FRAMES) {
      requestAnimationFrame(seat)
      return
    }
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    found?.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' })
  }
  requestAnimationFrame(seat)
}
